import { Controller } from "@hotwired/stimulus"

// Inline priority checkbox.
//   defer=false (default) — row-level toggle. Posts to update_inline
//     immediately and updates the flag in place.
//   defer=true — panel toggle. Tracks dirty state and dispatches
//     panel:dirty so the panel-form Save button enables. Listens for
//     panel:save-all to flush, panel:cancel-all to revert.
export default class extends Controller {
  static values = { url: String, taskId: Number, defer: Boolean }
  static targets = ["checkbox"]

  connect() {
    if (this.deferValue) {
      this.originalChecked = this.checkboxTarget.checked
      this.wasDirty = false
      this.boundSaveAll = () => this.flushNow()
      this.boundCancelAll = () => this.revert()
      document.addEventListener("panel:save-all", this.boundSaveAll)
      document.addEventListener("panel:cancel-all", this.boundCancelAll)
    }
  }

  disconnect() {
    if (this.deferValue) {
      document.removeEventListener("panel:save-all", this.boundSaveAll)
      document.removeEventListener("panel:cancel-all", this.boundCancelAll)
    }
  }

  toggle(event) {
    event.stopPropagation()
    if (this.deferValue) {
      const isDirty = this.checkboxTarget.checked !== this.originalChecked
      if (isDirty !== this.wasDirty) {
        this.wasDirty = isDirty
        document.dispatchEvent(new CustomEvent("panel:dirty", {
          detail: { dirty: isDirty }
        }))
      }
      return
    }
    const newValue = this.checkboxTarget.checked ? "high" : "medium"
    this._save(newValue)
  }

  stopPropagation(event) {
    // The wrapping <label> sits inside the row's click->openSidePanel
    // handler. We don't want to open the panel when the user is just
    // toggling priority.
    event.stopPropagation()
  }

  // Panel-form Save: PATCH the current checkbox state. No-op if not dirty.
  async flushNow() {
    if (!this.deferValue) return
    if (this.checkboxTarget.checked === this.originalChecked) return
    const newValue = this.checkboxTarget.checked ? "high" : "medium"
    await this._save(newValue)
    this.originalChecked = this.checkboxTarget.checked
    this.wasDirty = false
  }

  // Panel-form Cancel: restore original state.
  revert() {
    if (!this.deferValue) return
    this.checkboxTarget.checked = this.originalChecked
    this.wasDirty = false
  }

  async _save(newValue) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        body: JSON.stringify({ field: "priority", value: newValue })
      })
      if (!res.ok) {
        // Revert on failure
        this.checkboxTarget.checked = !this.checkboxTarget.checked
        return
      }
      this._updateFlag(newValue)
      this.dispatch("changed", { detail: { taskId: this.taskIdValue, value: newValue } })
    } catch (e) {
      this.checkboxTarget.checked = !this.checkboxTarget.checked
    }
  }

  // Show or hide the red flag next to the title without a page reload.
  // The flag is rendered server-side when priority=high; we mirror that
  // by inserting/removing the same markup on the row.
  _updateFlag(value) {
    const row = this.element.closest(".task-row")
    if (!row) return
    const titleCell = row.querySelector(".cell-title")
    if (!titleCell) return
    const existing = titleCell.querySelector("[data-priority-flag]")
    if (value === "high") {
      if (!existing) {
        const flag = document.createElement("span")
        flag.setAttribute("data-priority-flag", "true")
        flag.setAttribute("title", "High priority")
        flag.className = "inline-flex items-center justify-center text-[#E57373] flex-shrink-0"
        flag.style.cssText = "width: 14px; height: 14px;"
        flag.innerHTML = '<svg fill="currentColor" viewBox="0 0 20 20" style="width: 14px; height: 14px;"><path fill-rule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 01.8 1.6L13.25 8l3.55 4.4a1 1 0 01-.8 1.6H5v4a1 1 0 11-2 0V3z" clip-rule="evenodd"/></svg>'
        titleCell.insertBefore(flag, titleCell.firstChild)
      }
    } else if (existing) {
      existing.remove()
    }
  }
}
